import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  makeStyles,
} from "@material-ui/core";
import React from "react";
import { carecentreTypeScale as type } from "../../typeScale";

const zen = {
  primaryFaint: "rgba(7, 101, 255, 0.055)",
  bgBone: "#EFEEE9",
  paper: "#FFFFFF",
  text: "#15191F",
  muted: "#797160",
  line: "#E3E1DB",
};

const useStyles = makeStyles(() => ({
  wrap: {
    overflow: "auto",
    border: `1px solid ${zen.line}`,
    borderRadius: 10,
    background: zen.paper,
    boxShadow: "none",
  },
  table: {
    minWidth: 720,
    borderCollapse: "separate",
    borderSpacing: 0,
  },
  headCell: {
    background: zen.bgBone,
    borderBottom: `1px solid ${zen.line}`,
    color: zen.muted,
    fontFamily:
      "'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace",
    fontSize: type.label.fontSize,
    fontWeight: type.label.fontWeight,
    letterSpacing: "0.12em",
    lineHeight: `${type.label.lineHeight}px`,
    padding: "10px 16px",
    textTransform: "uppercase",
    whiteSpace: "nowrap",
    "&:not(:last-child)": {
      borderRight: "1px solid rgba(234, 228, 210, 0.45)",
    },
  },
  row: {
    background: zen.paper,
    transition: "background-color 120ms ease",
    "&:hover $bodyCell": {
      background: zen.primaryFaint,
    },
  },
  clickableRow: {
    cursor: "pointer",
  },
  bodyCell: {
    borderBottom: `1px solid ${zen.line}`,
    color: zen.text,
    fontSize: type.body.fontSize,
    lineHeight: `${type.body.lineHeight}px`,
    padding: "10px 16px",
    verticalAlign: "middle",
  },
  emptyCell: {
    color: zen.muted,
    fontSize: type.body.fontSize,
    padding: "24px 18px",
    textAlign: "center",
  },
  loadingLine: {
    height: 12,
    width: "70%",
    borderRadius: 999,
    background: zen.bgBone,
  },
}));

const CcDataTable = ({
  columns = [],
  rows = [],
  getRowKey,
  onRowClick,
  className,
  emptyMessage = "No data found",
  loading = false,
  loadingRows = 3,
  stickyHeader = true,
}) => {
  const classes = useStyles();
  const colSpan = Math.max(columns.length, 1);

  return (
    <TableContainer
      className={[classes.wrap, className].filter(Boolean).join(" ")}
    >
      <Table stickyHeader={stickyHeader} className={classes.table}>
        <TableHead>
          <TableRow>
            {columns.map((column) => (
              <TableCell
                key={column.key}
                align={column.align || "left"}
                className={classes.headCell}
                style={column.width ? { width: column.width } : undefined}
              >
                {column.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            Array.from({ length: loadingRows }).map((_, rowIndex) => (
              <TableRow key={`loading-${rowIndex}`} className={classes.row}>
                {columns.map((column) => (
                  <TableCell key={column.key} className={classes.bodyCell}>
                    <Box className={classes.loadingLine} />
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : rows.length > 0 ? (
            rows.map((row, index) => (
              <TableRow
                key={getRowKey ? getRowKey(row, index) : (row.id ?? index)}
                className={[classes.row, onRowClick ? classes.clickableRow : ""]
                  .filter(Boolean)
                  .join(" ")}
                hover={Boolean(onRowClick)}
                onClick={onRowClick ? () => onRowClick(row, index) : undefined}
              >
                {columns.map((column) => (
                  <TableCell
                    key={column.key}
                    align={column.align || "left"}
                    className={classes.bodyCell}
                  >
                    {column.render
                      ? column.render(row, index)
                      : row[column.key]}
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={colSpan} className={classes.emptyCell}>
                {emptyMessage}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default CcDataTable;
